import { useParams, useNavigate } from "react-router-dom";
import { useFund } from "@/contexts/FundContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Landmark, LayoutDashboard, Globe, PieChart,
  GitCompare, Terminal, BookOpen, Radio,
} from "lucide-react";

// Maps each app card to the `allowed_modules` slug used on the user record.
// Investors are hard-coded to `tychi_portal` only; super_admin bypasses
// the check; everyone else (org_admin / admin) must have the module
// listed in user.allowed_modules to access the app.
const APP_MODULE_MAP = {
  portal: "tychi_portal",
  bank: "tychi_bank",
  vault: "tychi_vault",
  allocator: "tychi_allocator",
  recon: "tychi_recon",
  command: "tychi_command",
  gl: "tychi_gl",
  radar: "tychi_radar",
};

function isAppEnabled(appKey, user) {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  if (user.role === "investor") return appKey === "portal";
  const required = APP_MODULE_MAP[appKey];
  return Array.isArray(user.allowed_modules) && user.allowed_modules.includes(required);
}

const TYCHI_APPS = [
  {
    key: "portal",
    name: "Tychi Portal",
    description: "Investor portal, documents, transactions, and reporting",
    icon: LayoutDashboard,
    color: "text-violet-400",
    bg: "bg-violet-500/10 border-violet-500/20 hover:border-violet-500/40",
    iconBg: "bg-violet-500/15",
    url: null, // internal route
  },
  {
    key: "allocator",
    name: "Tychi Allocator",
    description: "Asset allocation and portfolio optimization",
    icon: PieChart,
    color: "text-amber-400",
    bg: "bg-amber-500/10 border-amber-500/20 hover:border-amber-500/40",
    iconBg: "bg-amber-500/15",
    url: "https://allocator.tychi.co",
  },
  {
    key: "vault",
    name: "Tychi Vault",
    description: "Global fund operations and compliance",
    icon: Globe,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10 border-emerald-500/20 hover:border-emerald-500/40",
    iconBg: "bg-emerald-500/15",
    url: "https://live.tychi.co/auth/sign-in",
    loginTarget: "vault",
  },
  {
    key: "command",
    name: "Tychi Command",
    description: "Workflow automation and task orchestration",
    icon: Terminal,
    color: "text-rose-400",
    bg: "bg-rose-500/10 border-rose-500/20 hover:border-rose-500/40",
    iconBg: "bg-rose-500/15",
    url: "https://command.tychi.co",
  },
  {
    key: "recon",
    name: "Tychi Recon",
    description: "Reconciliation and data matching",
    icon: GitCompare,
    color: "text-cyan-400",
    bg: "bg-cyan-500/10 border-cyan-500/20 hover:border-cyan-500/40",
    iconBg: "bg-cyan-500/15",
    url: "https://recon.tychi.co/login",
  },
  {
    key: "gl",
    name: "Tychi GL",
    description: "General ledger and accounting",
    icon: BookOpen,
    color: "text-orange-400",
    bg: "bg-orange-500/10 border-orange-500/20 hover:border-orange-500/40",
    iconBg: "bg-orange-500/15",
    url: "https://live.tychi.co",
  },
  {
    key: "radar",
    name: "Tychi Radar",
    description: "Monitoring, alerts, and analytics",
    icon: Radio,
    color: "text-teal-400",
    bg: "bg-teal-500/10 border-teal-500/20 hover:border-teal-500/40",
    iconBg: "bg-teal-500/15",
    url: "https://radar.tychi.co",
  },
  {
    // Renamed from "Tychi Bank" → "Tychi Commit" (2026-09-21, UI-only).
    // Card keeps its original key/icon/styling/URL so module mapping
    // (tychi_bank) and click behavior are untouched.
    key: "bank",
    name: "Tychi Commit",
    description: "Banking operations and treasury management",
    icon: Landmark,
    color: "text-blue-400",
    bg: "bg-blue-500/10 border-blue-500/20 hover:border-blue-500/40",
    iconBg: "bg-blue-500/15",
    url: "https://bank.tychi.co",
  },
];

export default function AppSelector() {
  const { fundId } = useParams();
  const navigate = useNavigate();
  const { activeFund, fundLoading } = useFund();
  const { user } = useAuth();

  const handleAppClick = (app) => {
    if (app.key === "portal") {
      navigate(`/super-admin/funds/${fundId}/portal`);
      return;
    }
    if (app.url) {
      const params = new URLSearchParams();
      if (fundId) params.set("fund_id", fundId);
      if (app.loginTarget) params.set("loginTarget", app.loginTarget);
      const qs = params.toString();
      window.open(qs ? `${app.url}?${qs}` : app.url, "_blank");
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="app-selector-page">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/super-admin")}
          data-testid="back-to-master"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {fundLoading ? (
              <span className="inline-block w-48 h-8 bg-secondary rounded animate-pulse" />
            ) : (
              activeFund?.name || "Select Application"
            )}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Choose an application to manage this fund
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {TYCHI_APPS.map((app) => {
          const enabled = isAppEnabled(app.key, user);
          return (
            <Card
              key={app.key}
              className={`relative transition-all duration-200 ${app.bg} ${
                enabled
                  ? "cursor-pointer hover:shadow-[0_0_30px_rgba(108,71,196,0.12)] hover:scale-[1.02]"
                  : "opacity-50 cursor-not-allowed pointer-events-none"
              }`}
              onClick={enabled ? () => handleAppClick(app) : undefined}
              data-testid={`app-tile-${app.key}`}
              data-enabled={enabled ? "true" : "false"}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl ${app.iconBg} flex items-center justify-center`}>
                    <app.icon className={`w-6 h-6 ${app.color}`} strokeWidth={1.5} />
                  </div>
                  {enabled ? (
                    <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px] uppercase tracking-wider">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground border-border/50 text-[10px] uppercase tracking-wider">
                      Restricted
                    </Badge>
                  )}
                </div>
                <h3 className="font-heading font-semibold text-base mb-1">{app.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{app.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
